import type { CSSProperties } from 'react';

/**
 * LifecycleBar — a horizontal, numbered stage timeline for an entity's status
 * flow (e.g. a tax bill: no activity → received → processed → approved → paid).
 *
 * Stages up to and including `current` render as completed/active on the brand
 * color; later stages are muted. When a flow goes off-track (a voided bill),
 * pass `terminated` — every stage dims and the terminal state is called out.
 *
 * ⚠️ A STAGE THE STATUS CLAIMS BUT THE RECORD DOES NOT (punch-list §5).
 *
 * The bar used to derive everything from `current` alone, so a bill stored as
 * `paid` drew five confident ticks whether or not anything had actually been
 * processed or transmitted — the reviewer's exact complaint: "a tax bill reads
 * paid while the processed and transmitted dates are both blank". Status is a
 * stored, governed column with money hanging off it, so it stays the thing that
 * positions the bar; what changes is that the bar no longer VOUCHES for a step
 * with nothing behind it.
 *
 * Pass `evidence` on a stage — the date (or other record) that would exist if that
 * step really happened. A passed stage whose evidence is explicitly `null` renders
 * as UNRECORDED (a muted dash, not a tick) and the bar notes it underneath. Omit the
 * key entirely and the stage behaves exactly as it always has.
 *
 * ⚠️ UNRECORDED IS NOT "DIDN'T HAPPEN", AND THE WORDING IS LOAD-BEARING. The first
 * version drew these amber and said "nothing on record shows processed happened",
 * which reads as an accusation. Measured against the live demo, that fired on 16 of
 * 16 paid bills — because `processed_date` / `transmitted_date` arrived in migration
 * 109 with no backfill and are written by exactly ONE path (`processBill`), so a
 * seeded, imported or corrected bill has no stamp BY CONSTRUCTION. Absence of a
 * record is not evidence of absence; a bar that cries wolf on nearly every row
 * teaches people to ignore it, which is worse than the over-claiming it replaced.
 * So it states the fact — "no date on record" — and draws no conclusion.
 *
 * Scrolls horizontally inside its own container so a 5-stage bar never forces
 * the page wide on a phone.
 */

export interface LifecycleStage {
  key: string;
  label: string;
  /**
   * The record that proves this step happened — a date string, or `null` when the
   * caller HAS such a field and it is empty. Leave the key off when the stage has
   * no evidence field at all (`undefined` means "not claimed either way").
   */
  evidence?: string | null;
}

export interface LifecycleBarProps {
  stages: LifecycleStage[];
  current: string;
  /** When set, the flow ended off-track (e.g. voided): all stages dim and this
   *  label is called out as the terminal state. */
  terminated?: { label: string } | null;
}

export function LifecycleBar({ stages, current, terminated }: LifecycleBarProps) {
  const currentIndex = stages.findIndex((s) => s.key === current);

  // Stages the stored status says are behind us, that carry an evidence field, and
  // whose evidence is empty. `'evidence' in s` is load-bearing: an absent key means
  // the caller never claimed there was a record to check, which is not the same
  // thing as a record that is missing.
  const unconfirmed = terminated
    ? []
    : stages.filter((s, i) => i <= currentIndex && 'evidence' in s && !s.evidence);

  return (
    <div style={{ overflowX: 'auto', padding: 'var(--sp-1) 2px' }}>
      <ol style={{
        display: 'flex', alignItems: 'flex-start', listStyle: 'none',
        margin: 0, padding: 0, minWidth: 'min-content',
      }}>
        {stages.map((stage, i) => {
          const done = !terminated && i < currentIndex;
          const isCurrent = !terminated && i === currentIndex;
          const active = done || isCurrent;
          // Behind the current status, but with no record on file. Drawn muted rather
          // than ticked — the bar declines to vouch for it, and declines to accuse it.
          const unrecorded = active && 'evidence' in stage && !stage.evidence;
          const circle: CSSProperties = {
            width: 28, height: 28, borderRadius: '50%',
            display: 'grid', placeItems: 'center', flexShrink: 0,
            fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)',
            background: active && !unrecorded ? 'var(--brand-primary)' : 'var(--color-card)',
            color: active && !unrecorded ? 'var(--brand-on-primary)' : 'var(--color-muted)',
            border: '2px solid ' + (active && !unrecorded ? 'var(--brand-primary)' : 'var(--color-border)'),
          };
          return (
            <li key={stage.key} style={{
              display: 'flex', alignItems: 'center',
              flex: i < stages.length - 1 ? 1 : '0 0 auto',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '0 6px' }}>
                <div style={circle}>{unrecorded ? '–' : (done ? '✓' : i + 1)}</div>
                <span style={{
                  fontSize: 'var(--fs-xs)', textAlign: 'center', whiteSpace: 'nowrap',
                  color: unrecorded ? 'var(--color-muted)' : (active ? 'var(--color-ink)' : 'var(--color-muted)'),
                  fontWeight: isCurrent ? 'var(--fw-semibold)' : 'var(--fw-regular)',
                }}>{stage.label}</span>
              </div>
              {i < stages.length - 1 && (
                <div aria-hidden="true" style={{
                  flex: 1, height: 2, minWidth: 24, marginTop: 13,
                  background: done ? 'var(--brand-primary)' : 'var(--color-border)',
                }} />
              )}
            </li>
          );
        })}
      </ol>
      {terminated && (
        <div style={{
          marginTop: 'var(--sp-2)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
          color: 'var(--color-status-red)',
        }}>{terminated.label}</div>
      )}
      {unconfirmed.length > 0 && (
        <div
          role="status"
          style={{
            marginTop: 'var(--sp-2)', fontSize: 'var(--fs-sm)',
            color: 'var(--color-ink-3)',
          }}
        >
          {/* When the ONLY unrecorded stage is the one the status is sitting on, naming
              it twice reads as a stutter ("Marked Transmitted. No date is on record for
              transmitted.") — which is the common shape for a correction bill, born at
              `transmitted` with no stamp. Say it once. */}
          {unconfirmed.length === 1 && unconfirmed[0] === stages[currentIndex] ? (
            <>Marked <strong>{stages[currentIndex]?.label ?? current}</strong>, but no date is on record for it.</>
          ) : (
            <>
              Marked <strong>{stages[currentIndex]?.label ?? current}</strong>. No date is on record
              for {unconfirmed.map((s) => s.label.toLowerCase()).join(' or ')}.
            </>
          )}
        </div>
      )}
    </div>
  );
}
